import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AppsIcon from '@mui/icons-material/Apps';
import CableIcon from '@mui/icons-material/Cable';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import DnsIcon from '@mui/icons-material/Dns';
import InventoryIcon from '@mui/icons-material/Inventory';
import LanIcon from '@mui/icons-material/Lan';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import {
  asArray,
  BRAND_BLUE,
  DARK_TEXT,
  DataPills,
  EmptyState,
  ErrorState,
  formatNumber,
  hasMeaningfulValue,
  hasValue,
  KeyValueGrid,
  LoadingState,
  MetricCard,
  OsintShell,
  pick,
  SectionCard,
  shellCardSx,
  SLATE_TEXT,
  stringifyValue,
  titleCase,
  truncate,
  useOsintFetch,
  WARNING_AMBER,
} from './osintCommon';

const PRODUCT_PAGE_SIZE = 6;
const CANDIDATE_PAGE_SIZE = 3;
const DNS_PAGE_SIZE = 5;
const TECH_PAGE_SIZE = 6;
const PORT_PAGE_SIZE = 5;

const flattenTechStack = (techStack) => {
  if (Array.isArray(techStack)) return techStack;
  if (!techStack || typeof techStack !== 'object') return [];
  return Object.entries(techStack).flatMap(([key, value]) =>
    asArray(value).map((item) => (item && typeof item === 'object' ? { source: titleCase(key), ...item } : item))
  );
};

const itemTitle = (item, fallback = 'Untitled') => {
  if (typeof item !== 'object' || !item) return String(item || fallback);
  return (
    pick(item, ['name', 'title', 'product', 'application', 'app_name', 'appName'], '') ||
    [item.vendor, item.product_name || item.productName].filter(Boolean).join(' ') ||
    fallback
  );
};

const itemDescription = (item) => {
  if (typeof item !== 'object' || !item) return '';
  return pick(item, ['description', 'summary', 'details', 'about'], '');
};

const itemUrl = (item) => {
  if (typeof item !== 'object' || !item) return '';
  return pick(item, ['url', 'website', 'link', 'app_url', 'appUrl', 'store_url', 'storeUrl'], '');
};

const itemSource = (item) => {
  if (typeof item !== 'object' || !item) return '';
  return pick(item, ['source', 'provider', 'source_name', 'sourceName', 'platform', 'store'], '');
};

const itemCategory = (item, fallback = '') => {
  if (typeof item !== 'object' || !item) return fallback;
  return pick(item, ['category', 'type', 'product_type', 'productType', 'platform'], fallback);
};

const getCandidateReason = (item, kind) => {
  const text = `${itemTitle(item)} ${itemDescription(item)} ${itemUrl(item)}`.toLowerCase();
  if (kind === 'mobile') {
    if (text.includes('login') || text.includes('account') || text.includes('portal')) {
      return 'This mobile app may expose authentication, account workflows, or customer-facing functionality that deserves mobile application review.';
    }
    return 'This appears to be a public mobile application tied to the organization. Consider reviewing authentication, API traffic, data storage, and privacy handling.';
  }
  if (text.includes('admin') || text.includes('dashboard') || text.includes('portal')) {
    return 'This looks like a portal or administrative surface. It may warrant focused testing for authentication, authorization, and workflow abuse.';
  }
  if (text.includes('login') || text.includes('app.') || text.includes('platform')) {
    return 'This appears to be a public web application surface. Consider validating access control, business logic, session handling, and exposed functionality.';
  }
  return 'This public application may be worth reviewing as a candidate for focused application security testing.';
};

const CandidateCard = ({ item, kind, Icon, accent }) => {
  const title = itemTitle(item, kind === 'mobile' ? 'Mobile application' : 'Web application');
  const description = itemDescription(item);
  const url = itemUrl(item);
  const source = itemSource(item);
  const category = itemCategory(item, kind === 'mobile' ? 'Mobile App' : 'Web App');
  const reason = getCandidateReason(item, kind);

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${alpha(accent, 0.16)}`,
        bgcolor: '#ffffff',
        boxShadow: `0 16px 34px ${alpha(accent, 0.08)}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 5, bgcolor: accent }} />
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1.4} alignItems="flex-start">
          <Avatar sx={{ bgcolor: alpha(accent, 0.1), color: accent, border: `1px solid ${alpha(accent, 0.16)}`, flexShrink: 0 }}>
            <Icon />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip size="small" label={kind === 'mobile' ? 'Mobile Review Candidate' : 'Web App Candidate'} sx={{ fontWeight: 900, color: accent, bgcolor: alpha(accent, 0.08) }} />
              {category ? <Chip size="small" label={category} sx={{ fontWeight: 800 }} /> : null}
              {source ? <Chip size="small" label={`Source: ${source}`} sx={{ fontWeight: 800 }} /> : null}
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 950, color: DARK_TEXT, wordBreak: 'break-word', lineHeight: 1.2 }}>
              {title}
            </Typography>
            {url ? (
              <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-block', mt: 0.5, fontWeight: 850, color: accent, wordBreak: 'break-word' }}>
                {url}
              </Link>
            ) : null}
            {description ? (
              <Typography variant="body2" sx={{ mt: 1.1, color: SLATE_TEXT, lineHeight: 1.7, display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {description}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mt: 1.1, color: SLATE_TEXT, lineHeight: 1.7 }}>
                No product description was returned, but the application was discovered as part of the OSINT collection.
              </Typography>
            )}
            <Box sx={{ mt: 1.4, p: 1.35, borderRadius: 2.5, bgcolor: alpha(accent, 0.05), border: `1px solid ${alpha(accent, 0.12)}` }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 950, color: accent, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Why it matters
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.45, color: DARK_TEXT, lineHeight: 1.55 }}>
                {reason}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.35 }}>
              {url ? (
                <Button
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900, bgcolor: accent }}
                >
                  Open {kind === 'mobile' ? 'Listing' : 'App'}
                </Button>
              ) : null}
              <Chip size="small" label={kind === 'mobile' ? 'Check mobile APIs' : 'Consider web pentest'} sx={{ height: 32, fontWeight: 900, color: accent, bgcolor: alpha(accent, 0.08) }} />
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const CandidateSection = ({ eyebrow, title, description, items, kind, Icon, accent }) => {
  const rows = asArray(items).filter(hasMeaningfulValue);
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / CANDIDATE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleRows = rows.slice(currentPage * CANDIDATE_PAGE_SIZE, currentPage * CANDIDATE_PAGE_SIZE + CANDIDATE_PAGE_SIZE);

  return (
    <SectionCard
      eyebrow={eyebrow}
      title={title}
      description={description}
      action={pageCount > 1 ? (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="caption" sx={{ color: SLATE_TEXT, fontWeight: 800 }}>
            {currentPage + 1} / {pageCount}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={currentPage === 0}
            onClick={() => setPage((previous) => Math.max(0, previous - 1))}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPage((previous) => Math.min(pageCount - 1, previous + 1))}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
          >
            Next
          </Button>
        </Stack>
      ) : null}
    >
      {rows.length ? (
        <Grid container spacing={1.8}>
          {visibleRows.map((item, index) => (
            <Grid item xs={12} lg={4} key={`${itemTitle(item, title)}-${currentPage}-${index}`}>
              <CandidateCard item={item} kind={kind} Icon={Icon} accent={accent} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState title={`No ${title.toLowerCase()} returned`} />
      )}
    </SectionCard>
  );
};

const ProductCard = ({ item, category, Icon, accent }) => {
  const title = itemTitle(item, category);
  const description = itemDescription(item);
  const url = itemUrl(item);

  return (
    <Card sx={{ height: '100%', borderRadius: 3, border: `1px solid ${alpha(accent, 0.14)}`, boxShadow: 'none', bgcolor: '#ffffff' }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Avatar sx={{ bgcolor: alpha(accent, 0.1), color: accent, flexShrink: 0 }}>
            <Icon />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Chip size="small" label={category} sx={{ mb: 1, fontWeight: 900, color: accent, bgcolor: alpha(accent, 0.08) }} />
            <Typography sx={{ fontWeight: 950, color: DARK_TEXT, wordBreak: 'break-word', lineHeight: 1.25 }}>
              {title}
            </Typography>
            {description ? (
              <Typography variant="body2" sx={{ mt: 0.8, color: SLATE_TEXT, lineHeight: 1.7 }}>
                {truncate(description, 180)}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
              {url ? (
                <Button
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900, bgcolor: accent }}
                >
                  Open
                </Button>
              ) : null}
              {typeof item === 'object' && item?.platform ? <Chip size="small" label={item.platform} sx={{ fontWeight: 800 }} /> : null}
              {typeof item === 'object' && item?.category ? <Chip size="small" label={item.category} sx={{ fontWeight: 800 }} /> : null}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ProductSection = ({ eyebrow, title, description, items, category, Icon, accent }) => {
  const rows = asArray(items).filter(hasMeaningfulValue);
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / PRODUCT_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleRows = rows.slice(currentPage * PRODUCT_PAGE_SIZE, currentPage * PRODUCT_PAGE_SIZE + PRODUCT_PAGE_SIZE);

  return (
    <SectionCard
      eyebrow={eyebrow}
      title={title}
      description={description}
      action={pageCount > 1 ? (
        <Stack direction="row" spacing={0.75}>
          <Button
            size="small"
            variant="outlined"
            disabled={currentPage === 0}
            onClick={() => setPage((previous) => Math.max(0, previous - 1))}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPage((previous) => Math.min(pageCount - 1, previous + 1))}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
          >
            Next
          </Button>
        </Stack>
      ) : null}
    >
      {rows.length ? (
        <Grid container spacing={1.5}>
          {visibleRows.map((item, index) => (
            <Grid item xs={12} md={6} xl={4} key={`${itemTitle(item, category)}-${currentPage}-${index}`}>
              <ProductCard item={item} category={category} Icon={Icon} accent={accent} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState title={`No ${title.toLowerCase()} returned`} />
      )}
    </SectionCard>
  );
};

const SignalCard = ({ label, value, Icon, accent }) => (
  <Box sx={{ p: 1.6, height: '100%', borderRadius: 3, border: `1px solid ${alpha(accent, 0.14)}`, bgcolor: alpha(accent, 0.04) }}>
    <Avatar sx={{ bgcolor: alpha(accent, 0.12), color: accent, mb: 1 }}>
      <Icon />
    </Avatar>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: accent, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.5, fontWeight: 950, color: DARK_TEXT, wordBreak: 'break-word' }}>
      {formatNumber(value)}
    </Typography>
  </Box>
);

const cleanValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const firstValue = (object, keys, fallback = '') => {
  if (!object || typeof object !== 'object') return fallback;
  for (const key of keys) {
    const value = cleanValue(object[key]);
    if (value) return value;
  }
  return fallback;
};

const uniqueValues = (values) => Array.from(new Set(asArray(values).map((value) => cleanValue(value)).filter(Boolean)));

const normalizeDnsRecord = (record) => {
  if (!record || typeof record !== 'object') {
    return { type: 'DNS', name: cleanValue(record), target: '', ttl: '', raw: cleanValue(record) };
  }
  const type = firstValue(record, ['dnstype', 'Dnstype', 'dnsType', 'type_name', 'typeName', 'rrsettype', 'Rrsettype', 'type', 'Type'], 'DNS');
  const name = firstValue(record, ['name', 'Name', 'host', 'Host', 'domain', 'Domain'], '');
  const target = firstValue(record, ['address', 'Address', 'target', 'Target', 'value', 'Value', 'alias', 'Alias', 'additionalname', 'Additionalname', 'additionalName', 'exchange', 'Exchange'], '');
  const priority = firstValue(record, ['priority', 'Priority', 'preference', 'Preference'], '');
  const ttl = firstValue(record, ['ttl', 'Ttl'], '');
  const raw = firstValue(record, ['rawtext', 'Rawtext', 'rawText', 'record', 'Record'], '');
  return { type, name, target, priority, ttl, raw };
};

const normalizeIpRecord = (record) => {
  if (!record || typeof record !== 'object') return { ip: cleanValue(record), host: '', source: '' };
  return {
    ip: firstValue(record, ['ip', 'Ip', 'address', 'Address', 'value', 'Value'], ''),
    host: firstValue(record, ['host', 'Host', 'hostname', 'Hostname', 'domain', 'Domain', 'name', 'Name'], ''),
    source: firstValue(record, ['source', 'Source', 'provider', 'Provider', 'asn', 'Asn', 'org', 'Org', 'organization', 'Organization'], ''),
  };
};

const unwrapTechRecord = (record) => {
  if (!record || typeof record !== 'object') return record;
  return record.builtwith || record.Builtwith || record.builtWith || record.technology || record.Technology || record.tech || record;
};

const normalizeTechRecord = (record) => {
  const item = unwrapTechRecord(record);
  if (!item || typeof item !== 'object') return { name: cleanValue(item), tag: '', categories: [] };
  return {
    name: firstValue(item, ['name', 'Name', 'title', 'Title', 'technology', 'Technology', 'product', 'Product'], 'Unknown technology'),
    tag: firstValue(item, ['tag', 'Tag', 'category', 'Category', 'type', 'Type'], ''),
    categories: uniqueValues(item.categories || item.Categories || item.category_names || item.categoryNames || item.groups || item.Groups),
    description: firstValue(item, ['description', 'Description', 'summary', 'Summary'], ''),
    website: firstValue(item, ['website', 'Website', 'url', 'Url', 'link', 'Link'], ''),
  };
};

const normalizePortRecord = (record) => {
  if (!record || typeof record !== 'object') return { port: cleanValue(record), protocol: '', service: '', host: '', state: '' };
  return {
    port: firstValue(record, ['port', 'Port', 'number', 'Number', 'port_number', 'portNumber'], ''),
    protocol: firstValue(record, ['protocol', 'Protocol', 'proto', 'Proto', 'transport', 'Transport'], ''),
    service: firstValue(record, ['service', 'Service', 'name', 'Name', 'product', 'Product', 'banner', 'Banner'], ''),
    host: firstValue(record, ['host', 'Host', 'ip', 'Ip', 'address', 'Address', 'domain', 'Domain'], ''),
    state: firstValue(record, ['state', 'State', 'status', 'Status'], ''),
  };
};

const getDomainHost = (item) => {
  if (!item || typeof item !== 'object') return cleanValue(item);
  return firstValue(item, ['domain', 'Domain', 'host', 'Host', 'hostname', 'Hostname', 'name', 'Name', 'url', 'Url'], stringifyValue(item));
};

const getNestedDnsRecords = (item) => {
  if (!item || typeof item !== 'object') return [];
  return asArray(item.dns_records || item.DnsRecords || item.dnsRecords || item.records || item.Records || item.resolutions || item.Resolutions).map(normalizeDnsRecord);
};

const InfrastructurePage = () => {
  const { pentest_id } = useParams();
  const infraFetch = useOsintFetch('/pentests/osint/infrastructure', pentest_id, 'infrastructure');
  const productsFetch = useOsintFetch('/pentests/osint/products', pentest_id, 'products');
  const [subdomainSearch, setSubdomainSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dnsPage, setDnsPage] = useState(0);
  const [techPage, setTechPage] = useState(0);
  const [portPage, setPortPage] = useState(0);

  const infra = infraFetch.data || {};
  const products = productsFetch.data || {};
  const primaryDomain = pick(infra, ['primary_domain', 'primaryDomain', 'domain'], 'Unknown domain');
  const otherDomains = asArray(pick(infra, ['other_domains', 'otherDomains', 'domains'], []));
  const dnsRecords = asArray(pick(infra, ['dns_records', 'dnsRecords'], []));
  const reverseWhois = asArray(pick(infra, ['reverse_whois_domains', 'reverseWhoisDomains', 'reverse_whois'], []));
  const subdomains = asArray(pick(infra, ['subdomains', 'hosts'], []));
  const ips = asArray(pick(infra, ['ips', 'ip_addresses', 'ipAddresses'], []));
  const portScans = asArray(pick(infra, ['port_scans', 'portScans', 'ports'], []));
  const techStack = pick(infra, ['tech_stack', 'techStack', 'technologies'], []);
  const whois = pick(infra, ['whois', 'whois_record', 'whoisRecord'], {});
  const saasProducts = asArray(pick(products, ['saas_products', 'saasProducts'], []));
  const mobileApps = asArray(pick(products, ['mobile_apps', 'mobileApps'], []));
  const physicalProducts = asArray(pick(products, ['physical_products', 'physicalProducts'], []));

  const techValues = useMemo(() => flattenTechStack(techStack), [techStack]);
  const dnsRecordRows = useMemo(() => dnsRecords.map(normalizeDnsRecord).filter((record) => hasMeaningfulValue(record.name || record.target || record.raw)), [dnsRecords]);
  const ipRows = useMemo(() => ips.map(normalizeIpRecord).filter((record) => hasMeaningfulValue(record.ip || record.host)), [ips]);
  const techRows = useMemo(() => techValues.map(normalizeTechRecord).filter((record) => hasMeaningfulValue(record.name)), [techValues]);
  const portRows = useMemo(() => portScans.map(normalizePortRecord).filter((record) => hasMeaningfulValue(record.port || record.service || record.host)), [portScans]);
  const filteredSubdomains = useMemo(() => {
    const query = subdomainSearch.trim().toLowerCase();
    const filtered = query ? subdomains.filter((item) => {
      const host = getDomainHost(item);
      const dnsText = getNestedDnsRecords(item).map((record) => [record.type, record.name, record.target].filter(Boolean).join(' ')).join(' ');
      return `${host} ${dnsText}`.toLowerCase().includes(query);
    }) : subdomains;

    return [...filtered].sort((a, b) => {
      const aRecords = getNestedDnsRecords(a);
      const bRecords = getNestedDnsRecords(b);
      if (aRecords.length !== bRecords.length) return bRecords.length - aRecords.length;
      return getDomainHost(a).localeCompare(getDomainHost(b));
    });
  }, [subdomainSearch, subdomains]);

  if (infraFetch.loading || productsFetch.loading) return <LoadingState label="Loading infrastructure intelligence..." />;
  if (infraFetch.error) return <ErrorState message={infraFetch.error} />;
  if (productsFetch.error) return <ErrorState message={productsFetch.error} />;

  const pageRows = filteredSubdomains.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const dnsPageRows = dnsRecordRows.slice(dnsPage * DNS_PAGE_SIZE, dnsPage * DNS_PAGE_SIZE + DNS_PAGE_SIZE);
  const techPageRows = techRows.slice(techPage * TECH_PAGE_SIZE, techPage * TECH_PAGE_SIZE + TECH_PAGE_SIZE);
  const portPageRows = portRows.slice(portPage * PORT_PAGE_SIZE, portPage * PORT_PAGE_SIZE + PORT_PAGE_SIZE);
  const externalHostCount = 1 + subdomains.length + ips.length;
  const whoisSummary = [
    { label: 'Registrar', value: pick(whois, ['registrar', 'Registrar'], '') },
    { label: 'Created', value: pick(whois, ['creation_date', 'creationDate', 'created_date', 'createdDate', 'Creation Date'], '') },
    { label: 'Expires', value: pick(whois, ['expiration_date', 'expirationDate', 'expires', 'Expiration Date'], '') },
    { label: 'Registrant', value: pick(whois, ['org', 'organization', 'name', 'Org', 'Name'], '') },
  ].filter((item) => hasMeaningfulValue(item.value));

  return (
    <OsintShell>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="External" value={formatNumber(externalHostCount)} helper="Main domain plus discovered subdomains and IP addresses." Icon={DnsIcon} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Web Apps" value={formatNumber(saasProducts.length)} helper="SaaS and web application products discovered publicly." Icon={CloudIcon} accent="#0F766E" /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Mobile" value={formatNumber(mobileApps.length)} helper="Mobile applications associated with the company." Icon={AppsIcon} accent="#7C3AED" /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Physical Products" value={formatNumber(physicalProducts.length)} helper="Physical product references associated with the company." Icon={InventoryIcon} accent={WARNING_AMBER} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <SectionCard
            eyebrow="External"
            title="Internet-facing infrastructure"
            description="Main domain, possible related domains, subdomains with DNS evidence, public DNS records, IPs, technologies, and observed services."
          >
            <Grid container spacing={1.5} sx={{ mb: { xs: 3, md: 3.5 }, pb: 0.5 }}>
              <Grid item xs={12} sm={6} lg={2}><SignalCard label="Domains" value={1 + otherDomains.length} Icon={PublicIcon} accent={BRAND_BLUE} /></Grid>
              <Grid item xs={12} sm={6} lg={2}><SignalCard label="DNS" value={dnsRecordRows.length} Icon={StorageIcon} accent="#0F766E" /></Grid>
              <Grid item xs={12} sm={6} lg={2}><SignalCard label="Subdomains" value={subdomains.length} Icon={DnsIcon} accent="#2563EB" /></Grid>
              <Grid item xs={12} sm={6} lg={2}><SignalCard label="Tech Stack" value={techRows.length} Icon={CodeIcon} accent="#D97706" /></Grid>
              <Grid item xs={12} sm={6} lg={2}><SignalCard label="IPs" value={ipRows.length} Icon={LanIcon} accent="#7C3AED" /></Grid>
              <Grid item xs={12} sm={6} lg={2}><SignalCard label="Ports" value={portRows.length} Icon={CableIcon} accent="#0F766E" /></Grid>
            </Grid>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} lg={4}>
                <Card sx={{ height: '100%', borderRadius: 3, border: `1px solid ${alpha(BRAND_BLUE, 0.1)}`, boxShadow: 'none' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>
                      Domain footprint
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.6, fontWeight: 950, color: DARK_TEXT, wordBreak: 'break-word' }}>
                      {primaryDomain}
                    </Typography>
                    <Stack spacing={1.4} sx={{ mt: 1.4 }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: SLATE_TEXT, textTransform: 'uppercase' }}>Main domain</Typography>
                        <Box sx={{ mt: 0.8 }}><DataPills values={[primaryDomain, ...otherDomains]} /></Box>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: SLATE_TEXT, textTransform: 'uppercase' }}>Possible related domains</Typography>
                        <Box sx={{ mt: 0.8 }}><DataPills values={reverseWhois} accent="#7C3AED" limit={14} /></Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={8}>
                <Card sx={{ ...shellCardSx, overflow: 'hidden', boxShadow: 'none' }}>
                  <Box sx={{ p: 2.2, pb: 1.5 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>
                          Subdomains
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.6, fontWeight: 950, color: DARK_TEXT }}>
                          Evidence-first host inventory
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.4, color: SLATE_TEXT }}>
                          Hosts with DNS records are shown first so live or resolvable assets are easier to review.
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        value={subdomainSearch}
                        onChange={(event) => { setSubdomainSearch(event.target.value); setPage(0); }}
                        placeholder="Search subdomains..."
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        sx={{ width: { xs: '100%', md: 360 } }}
                      />
                    </Stack>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: BRAND_BLUE, color: '#ffffff', fontWeight: 900, borderBottom: 'none' } }}>
                          <TableCell>Host</TableCell>
                          <TableCell width={210}>DNS Records</TableCell>
                          <TableCell width={260}>Resolution</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pageRows.length ? pageRows.map((item, index) => {
                          const hostText = getDomainHost(item);
                          const nestedDns = getNestedDnsRecords(item);
                          const recordTypes = uniqueValues(nestedDns.map((record) => record.type)).slice(0, 4);
                          const targets = uniqueValues(nestedDns.map((record) => record.target)).slice(0, 3);
                          const url = /^https?:\/\//i.test(hostText) ? hostText : `https://${hostText}`;
                          return (
                            <TableRow key={`${hostText}-${index}`} hover sx={{ '&:nth-of-type(odd)': { bgcolor: alpha(BRAND_BLUE, 0.016) } }}>
                              <TableCell sx={{ fontWeight: 850, color: DARK_TEXT, wordBreak: 'break-word' }}>
                                <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ color: DARK_TEXT, fontWeight: 850 }}>
                                  {hostText}
                                </Link>
                              </TableCell>
                              <TableCell>
                                {nestedDns.length ? (
                                  <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                                    <Chip size="small" label={`${nestedDns.length} records`} sx={{ fontWeight: 850, color: '#0F766E', bgcolor: alpha('#0F766E', 0.08) }} />
                                    {recordTypes.map((type) => <Chip key={`${hostText}-${type}`} size="small" label={type} sx={{ fontWeight: 850 }} />)}
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" sx={{ color: SLATE_TEXT }}>No records returned</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {targets.length ? <DataPills values={targets} accent="#7C3AED" limit={3} /> : <Typography variant="body2" sx={{ color: SLATE_TEXT }}>No resolution shown</Typography>}
                              </TableCell>
                            </TableRow>
                          );
                        }) : (
                          <TableRow><TableCell colSpan={3}><EmptyState title="No subdomains match the search" /></TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Divider />
                  <TablePagination
                    component="div"
                    count={filteredSubdomains.length}
                    page={page}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionCard
                  eyebrow="DNS and IPs"
                  title="Main domain DNS records"
                  description="DNS records returned for the primary domain. Use this to quickly review mail, name server, text, and address records."
                >
                  <Stack spacing={1.5}>
                    {dnsRecordRows.length ? (
                      <>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ '& th': { bgcolor: alpha('#0F766E', 0.08), color: DARK_TEXT, fontWeight: 900, borderBottom: `1px solid ${alpha('#0F766E', 0.14)}` } }}>
                                <TableCell width={80}>Type</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Value</TableCell>
                                <TableCell width={110}>TTL</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dnsPageRows.map((record, index) => (
                                <TableRow key={`${record.type}-${record.name}-${record.target}-${dnsPage}-${index}`} hover>
                                  <TableCell><Chip size="small" label={record.type} sx={{ fontWeight: 900, color: '#0F766E', bgcolor: alpha('#0F766E', 0.08) }} /></TableCell>
                                  <TableCell sx={{ fontWeight: 800, color: DARK_TEXT, wordBreak: 'break-word' }}>{record.name || primaryDomain}</TableCell>
                                  <TableCell sx={{ color: SLATE_TEXT, wordBreak: 'break-word' }}>{record.target || truncate(record.raw, 120) || 'No value returned'}</TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                      {record.ttl ? <Chip size="small" label={record.ttl} sx={{ fontWeight: 800 }} /> : null}
                                      {record.priority ? <Chip size="small" label={`P${record.priority}`} sx={{ fontWeight: 800 }} /> : null}
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={dnsRecordRows.length}
                          page={dnsPage}
                          onPageChange={(_, nextPage) => setDnsPage(nextPage)}
                          rowsPerPage={DNS_PAGE_SIZE}
                          rowsPerPageOptions={[DNS_PAGE_SIZE]}
                        />
                      </>
                    ) : (
                      <EmptyState title="No DNS records returned" />
                    )}
                    {ipRows.length ? (
                      <>
                        <Divider />
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: '#7C3AED', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
                            Resolved IP addresses
                          </Typography>
                          <DataPills values={ipRows.map((record) => [record.ip, record.host].filter(Boolean).join(' - '))} accent="#7C3AED" limit={12} />
                        </Box>
                      </>
                    ) : null}
                  </Stack>
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionCard
                  eyebrow="Tech and Ports"
                  title="Technology and service signals"
                  description="Detected website technologies and observed services. Tables are paged to keep the infrastructure view scannable."
                >
                  <Stack spacing={1.5}>
                    {techRows.length ? (
                      <>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ '& th': { bgcolor: alpha('#D97706', 0.08), color: DARK_TEXT, fontWeight: 900, borderBottom: `1px solid ${alpha('#D97706', 0.14)}` } }}>
                                <TableCell>Technology</TableCell>
                                <TableCell width={150}>Category</TableCell>
                                <TableCell width={150}>Source</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {techPageRows.map((record, index) => (
                                <TableRow key={`${record.name}-${record.tag}-${techPage}-${index}`} hover>
                                  <TableCell sx={{ fontWeight: 850, color: DARK_TEXT, wordBreak: 'break-word' }}>
                                    {record.name}
                                    {record.description ? <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: SLATE_TEXT }}>{truncate(record.description, 90)}</Typography> : null}
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                      {record.tag ? <Chip size="small" label={record.tag} sx={{ fontWeight: 850, color: '#D97706', bgcolor: alpha('#D97706', 0.08) }} /> : null}
                                      {record.categories.slice(0, 2).map((category) => <Chip key={`${record.name}-${category}`} size="small" label={category} sx={{ fontWeight: 800 }} />)}
                                    </Stack>
                                  </TableCell>
                                  <TableCell sx={{ color: SLATE_TEXT }}>{record.website ? <Link href={record.website} target="_blank" rel="noopener noreferrer">Open</Link> : 'OSINT'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={techRows.length}
                          page={techPage}
                          onPageChange={(_, nextPage) => setTechPage(nextPage)}
                          rowsPerPage={TECH_PAGE_SIZE}
                          rowsPerPageOptions={[TECH_PAGE_SIZE]}
                        />
                      </>
                    ) : (
                      <EmptyState title="No technology stack returned" />
                    )}
                    {portRows.length ? (
                      <>
                        <Divider />
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: '#0F766E', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
                            Observed ports and services
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: alpha('#0F766E', 0.08), color: DARK_TEXT, fontWeight: 900, borderBottom: `1px solid ${alpha('#0F766E', 0.14)}` } }}>
                                  <TableCell width={90}>Port</TableCell>
                                  <TableCell>Service</TableCell>
                                  <TableCell width={130}>Host</TableCell>
                                  <TableCell width={90}>State</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {portPageRows.map((record, index) => (
                                  <TableRow key={`${record.host}-${record.port}-${portPage}-${index}`} hover>
                                    <TableCell><Chip size="small" label={record.port || 'n/a'} sx={{ fontWeight: 900, color: '#0F766E', bgcolor: alpha('#0F766E', 0.08) }} /></TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: DARK_TEXT, wordBreak: 'break-word' }}>{record.service || record.protocol || 'Unknown service'}</TableCell>
                                    <TableCell sx={{ color: SLATE_TEXT, wordBreak: 'break-word' }}>{record.host || '-'}</TableCell>
                                    <TableCell>{record.state ? <Chip size="small" label={record.state} sx={{ fontWeight: 800 }} /> : '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <TablePagination
                            component="div"
                            count={portRows.length}
                            page={portPage}
                            onPageChange={(_, nextPage) => setPortPage(nextPage)}
                            rowsPerPage={PORT_PAGE_SIZE}
                            rowsPerPageOptions={[PORT_PAGE_SIZE]}
                          />
                        </Box>
                      </>
                    ) : null}
                  </Stack>
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard
                  eyebrow="WHOIS"
                  title="Registration snapshot"
                  description="Quick registrar context for reference. This is intentionally compact because ownership records are usually supporting evidence."
                >
                  {hasValue(whois) ? (
                    <Stack spacing={1.5}>
                      {whoisSummary.length ? (
                        <Grid container spacing={1.25}>
                          {whoisSummary.map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.label}>
                              <Box sx={{ p: 1.35, borderRadius: 2.5, bgcolor: alpha('#64748b', 0.05), border: `1px solid ${alpha('#64748b', 0.1)}` }}>
                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', color: SLATE_TEXT }}>{item.label}</Typography>
                                <Typography sx={{ mt: 0.45, fontWeight: 850, color: DARK_TEXT, wordBreak: 'break-word' }}>{truncate(String(item.value), 80)}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      ) : null}
                      <KeyValueGrid items={Object.entries(whois).slice(0, 8).map(([key, value]) => ({ label: titleCase(key), value }))} />
                    </Stack>
                  ) : (
                    <EmptyState title="No WHOIS details returned" />
                  )}
                </SectionCard>
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <CandidateSection
            eyebrow="Web Application"
            title="Web applications worth reviewing"
            description="Publicly discovered web apps, portals, SaaS products, and login surfaces that may deserve focused testing or a dedicated web application pentest."
            items={saasProducts}
            kind="web"
            Icon={CloudIcon}
            accent={BRAND_BLUE}
          />
        </Grid>

        <Grid item xs={12}>
          <CandidateSection
            eyebrow="Mobile Applications"
            title="Mobile applications worth reviewing"
            description="Public mobile applications tied to the organization. These may expose mobile APIs, authentication flows, local storage, and privacy-sensitive workflows."
            items={mobileApps}
            kind="mobile"
            Icon={AppsIcon}
            accent="#7C3AED"
          />
        </Grid>

        <Grid item xs={12}>
          <ProductSection
            eyebrow="Physical Products"
            title="Physical product references"
            description="Hardware, appliances, physical products, or non-software product references discovered through OSINT collection."
            items={physicalProducts}
            category="Physical Product"
            Icon={InventoryIcon}
            accent={WARNING_AMBER}
          />
        </Grid>
      </Grid>
    </OsintShell>
  );
};

export default InfrastructurePage;
